/**
 * Supabase Client Configuration
 *
 * This module initializes and exports the Supabase client for database
 * operations and real-time subscriptions with anonymous UUID-based authentication.
 *
 * Environment variables required:
 * - VITE_SUPABASE_URL: Your Supabase project URL
 * - VITE_SUPABASE_ANON_KEY: Your Supabase anonymous key
 *
 * Authentication Model:
 * - No Supabase Auth - UUIDs generated client-side from passphrases
 * - User UUID stored in localStorage
 * - Application-level validation of user_id in queries
 * - RLS policies simplified to trust client (acceptable for anonymous platform)
 */

import { createClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import { getUserUUID } from '@/utils/anonymousAuth'

// Validate environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl) {
  throw new Error('Missing environment variable: VITE_SUPABASE_URL')
}

if (!supabaseAnonKey) {
  throw new Error('Missing environment variable: VITE_SUPABASE_ANON_KEY')
}

/**
 * Supabase client instance
 *
 * This client is typed with our Database schema for full TypeScript support.
 * It provides:
 * - Database queries (no RLS enforcement - trust client)
 * - Real-time subscriptions
 * - No authentication (UUID-based from localStorage)
 */
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Disable Supabase Auth - we use client-side UUID generation
    autoRefreshToken: false,
    persistSession: false,
    detectSessionInUrl: false,
  },
  // Enable real-time for posts and messages
  realtime: {
    params: {
      eventsPerSecond: 10, // Rate limit to prevent abuse
    },
  },
})

/**
 * Helper function to check if user is authenticated
 * (has UUID in localStorage)
 */
export function isAuthenticated(): boolean {
  return getUserUUID() !== null
}

/**
 * Helper function to get current user ID from localStorage
 */
export function getCurrentUserId(): string | null {
  return getUserUUID()
}
